import { readAdminSession } from '../admin/authProvider';
import { readProjectSettings } from '../admin/adminSettings';

export type PracticeSocialProofSettings = {
    enabled: boolean;
    minimumCompletedAttempts: number;
    requiredImprovementSteps: number;
    minimumImprovementPerStepPoints: number;
    minimumTotalImprovementPoints: number;
    minimumCurrentScoreOutOf90: number;
    minimumAppExperienceRating: number;
    promptCooldownDays: number;
    maximumPromptsPerUser: number;
    promptHeadline: string;
    promptMessage: string;
};

export type PracticeSocialProofSettingsSnapshot = {
    configuration: PracticeSocialProofSettings;
    isPersisted: boolean;
    updatedAtUtc: string;
    validationErrors: string[];
};

export const defaultPracticeSocialProofSettings: PracticeSocialProofSettings = {
    enabled: true,
    minimumCompletedAttempts: 30,
    requiredImprovementSteps: 2,
    minimumImprovementPerStepPoints: 2,
    minimumTotalImprovementPoints: 5,
    minimumCurrentScoreOutOf90: 50,
    minimumAppExperienceRating: 4,
    promptCooldownDays: 90,
    maximumPromptsPerUser: 3,
    promptHeadline: 'Your practice is paying off',
    promptMessage:
        'You have built real momentum across your recent attempts. Would you share your honest PTE Ultra experience with other learners?',
};

function asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function asNumber(value: unknown, fallback: number): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function asString(value: unknown, fallback = ''): string {
    return typeof value === 'string' ? value : fallback;
}

function asBoolean(value: unknown, fallback: boolean): boolean {
    return typeof value === 'boolean' ? value : fallback;
}

function normalizeConfiguration(value: unknown): PracticeSocialProofSettings {
    const row = asRecord(value);
    const fallback = defaultPracticeSocialProofSettings;
    return {
        enabled: asBoolean(row.enabled, fallback.enabled),
        minimumCompletedAttempts: asNumber(row.minimumCompletedAttempts, fallback.minimumCompletedAttempts),
        requiredImprovementSteps: asNumber(row.requiredImprovementSteps, fallback.requiredImprovementSteps),
        minimumImprovementPerStepPoints: asNumber(
            row.minimumImprovementPerStepPoints,
            fallback.minimumImprovementPerStepPoints,
        ),
        minimumTotalImprovementPoints: asNumber(
            row.minimumTotalImprovementPoints,
            fallback.minimumTotalImprovementPoints,
        ),
        minimumCurrentScoreOutOf90: asNumber(
            row.minimumCurrentScoreOutOf90,
            fallback.minimumCurrentScoreOutOf90,
        ),
        minimumAppExperienceRating: asNumber(
            row.minimumAppExperienceRating,
            fallback.minimumAppExperienceRating,
        ),
        promptCooldownDays: asNumber(row.promptCooldownDays, fallback.promptCooldownDays),
        maximumPromptsPerUser: asNumber(row.maximumPromptsPerUser, fallback.maximumPromptsPerUser),
        promptHeadline: asString(row.promptHeadline, fallback.promptHeadline),
        promptMessage: asString(row.promptMessage, fallback.promptMessage),
    };
}

function normalizeSnapshot(value: unknown): PracticeSocialProofSettingsSnapshot {
    const row = asRecord(value);
    return {
        configuration: normalizeConfiguration(row.configuration),
        isPersisted: asBoolean(row.isPersisted, false),
        updatedAtUtc: asString(row.updatedAtUtc),
        validationErrors: Array.isArray(row.validationErrors)
            ? row.validationErrors.filter((item): item is string => typeof item === 'string')
            : [],
    };
}

function getErrorMessage(payload: unknown, fallback: string): string {
    if (typeof payload === 'string' && payload.trim()) return payload;
    const row = asRecord(payload);
    const message = row.errorMessage ?? row.message ?? row.error ?? row.title ?? row.detail;
    return typeof message === 'string' && message.trim() ? message : fallback;
}

function getApiDetails() {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const configuredBase = String(
        import.meta.env.VITE_API_BASE_URL ??
            import.meta.env.VITE_API_URL ??
            settings.adminApiBaseUrl ??
            'https://test-api.pteultra.com/api/v1',
    ).replace(/\/+$/, '');
    const apiVersion = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');
    const apiRoot = /\/api\/v[^/]+$/i.test(configuredBase)
        ? configuredBase
        : `${configuredBase}/api/v${apiVersion}`;
    const token = String(
        session?.adminToken ??
            session?.accessToken ??
            import.meta.env.VITE_ADMIN_TOKEN ??
            settings.adminApiToken ??
            '',
    );
    return { url: `${apiRoot}/AdminSocialProofSettings`, token };
}

async function request(method: 'GET' | 'PUT', configuration?: PracticeSocialProofSettings) {
    const { url, token } = getApiDetails();
    const headers = new Headers({ Accept: 'application/json' });
    if (token) headers.set('Authorization', `Bearer ${token}`);
    if (configuration) headers.set('Content-Type', 'application/json');

    const response = await fetch(url, {
        method,
        headers,
        body: configuration ? JSON.stringify({ configuration }) : undefined,
    });
    const contentType = response.headers.get('content-type') ?? '';
    const payload: unknown = contentType.includes('application/json') ? await response.json() : await response.text();
    if (!response.ok) throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));

    const envelope = asRecord(payload);
    if (envelope.success === false) {
        throw new Error(getErrorMessage(envelope, 'Social proof settings request failed'));
    }
    return normalizeSnapshot('data' in envelope ? envelope.data : payload);
}

export function getPracticeSocialProofSettings() {
    return request('GET');
}

export function savePracticeSocialProofSettings(configuration: PracticeSocialProofSettings) {
    return request('PUT', configuration);
}

